<template>
    <Modal :isOpen="isOpen" @close="$emit('close')">
        <template #title>
            <h2 class="text-lg font-medium">Customize Connection Name</h2>
        </template>
        <template #content>
            <div class="flex flex-col gap-6 pt-3">
                <div
                    class="self-stretch justify-start text-sm font-normal leading-tight"
                >
                    Add custom text to the connection name to make it easier to
                    recognize in the list. This text will appear in parentheses
                    after the original name.
                </div>

                <div class="flex flex-col gap-2">
                    <UiInput
                        id="nickname-input"
                        name="nickname-input"
                        label="Custom Connection Name"
                    >
                        <template #input>
                            <input
                                type="text"
                                v-model="nickname"
                                placeholder="Enter a custom name for this connection"
                                class="block w-full bg-white rounded border border-slate-200 py-1.5 text-gray-900 placeholder:text-slate-500 placeholder:text-sm transition-colors focus:ring-0 focus:border-slate-800 hover:border-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                        </template>
                    </UiInput>
                </div>
            </div>
        </template>
        <template #errors>
            <div v-if="error" class="text-red-500 text-sm mt-2">
                {{ error }}
            </div>
        </template>
        <template #footer>
            <footer class="flex justify-between gap-2">
                <button
                    @click="$emit('close')"
                    class="px-4 py-2.5 bg-white rounded shadow-sm border border-gray-200 text-sm font-medium hover:bg-gray-50 active:bg-slate-200 focus:outline-slate-200 focus:border-slate-200 hover:border-slate-300 active:border-slate-300"
                    :disabled="isLoading"
                >
                    Cancel
                </button>
                <button
                    @click="saveNickname"
                    class="px-4 py-2.5 bg-gray-900 text-white rounded shadow-sm text-sm font-medium hover:bg-gray-800 flex items-center gap-2 active:bg-slate-200 focus:outline-slate-200 focus:border-slate-200 hover:border-slate-300 active:border-slate-300"
                    :disabled="isLoading"
                >
                    <span
                        v-if="isLoading"
                        class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                    ></span>
                    <span>Save name</span>
                </button>
            </footer>
        </template>
    </Modal>
</template>

<script setup>
import { ref, defineEmits, defineProps, onMounted } from 'vue';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import Modal from '@/components/ui/modal/ui-modal.vue';
import { notify } from 'notiwind';

const props = defineProps({
    connectionName: {
        type: String,
        default: '',
    },
    connectionGuid: {
        type: String,
        required: true,
    },
    isOpen: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['close', 'update-success']);

const nickname = ref('');
const error = ref('');
const isLoading = ref(false);

onMounted(() => {
    nickname.value = props.connectionName;
    error.value = '';
});

function saveNickname() {
    error.value = '';
    isLoading.value = true;

    const nicknameValue = nickname.value.trim() === '' ? null : nickname.value;

    ApiService.apiCall(`/members/${props.connectionGuid}/nickname`, 'post', {
        nickname: nicknameValue,
    })
        .then((response) => {
            if (response.data.success) {
                emit('update-success', {
                    guid: props.connectionGuid,
                    nickname: response.data.nickname,
                });
                emit('close');
            } else {
                error.value =
                    response.data.error || 'Failed to update nickname';
                showErrorNotification(error.value);
            }
        })
        .catch((err) => {
            let errorMessage = 'An error occurred while updating the nickname';

            if (err.response && err.response.data && err.response.data.error) {
                errorMessage = err.response.data.error;
            }

            error.value = errorMessage;
            showErrorNotification(errorMessage);
        })
        .finally(() => {
            isLoading.value = false;
        });
}

function showErrorNotification(message) {
    notify({
        group: 'error',
        title: 'Error Updating Nickname',
        text: message,
    });
}
</script>
